import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Sparkles, FormInput, Database, FilePen, HardDrive, Infinity as InfinityIcon, ArrowRight, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useUsage } from '../hooks/useUsage';

const fmtStorage = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const pctColor = (pct) => {
  if (pct >= 100) return 'bg-rose-500';
  if (pct >= 80)  return 'bg-orange-500';
  if (pct >= 50)  return 'bg-amber-500';
  return 'bg-emerald-500';
};

const textColor = (pct) => {
  if (pct >= 100) return 'text-rose-700';
  if (pct >= 80)  return 'text-orange-700';
  if (pct >= 50)  return 'text-amber-700';
  return 'text-emerald-700';
};

const MeterRow = ({ icon: Icon, label, current, limit, pct, isStorage = false, isUnlimited = false, resetInDays = null, testId }) => {
  const currentDisplay = isStorage ? fmtStorage(current) : current.toLocaleString();
  const limitDisplay   = isUnlimited
    ? 'Unlimited'
    : (isStorage ? fmtStorage(limit) : limit.toLocaleString());

  const resetLabel = resetInDays != null
    ? (resetInDays === 0 ? 'Resets today' : resetInDays === 1 ? 'Resets tomorrow' : `Resets in ${resetInDays} days`)
    : null;

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">{label}</span>
          {resetLabel && !isUnlimited && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium"
              data-testid={`${testId}-reset-countdown`}
              title={`Counter resets at the start of next month (UTC)`}
            >
              <RotateCcw className="w-3 h-3" />
              {resetLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {isUnlimited ? (
            <span className="font-semibold text-emerald-700 flex items-center gap-1">
              <InfinityIcon className="w-4 h-4" /> Unlimited
            </span>
          ) : (
            <>
              <span className={`font-mono font-semibold ${textColor(pct)}`}>{currentDisplay}</span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-600 font-mono">{limitDisplay}</span>
              <span className="text-slate-400 ml-2 text-xs">({pct}%)</span>
            </>
          )}
        </div>
      </div>
      {!isUnlimited && (
        <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 ${pctColor(pct)} transition-all duration-500`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      )}
    </div>
  );
};

export const UsageMeters = () => {
  const { usage, loading, isEnterprise } = useUsage();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card data-testid="usage-meters-loading">
        <CardHeader>
          <CardTitle>Your Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">Loading usage data…</p>
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  const { current, limits, percentages, upgrade_to, plan_display_name, unlimited = {}, period_reset } = usage;
  const isFormsUnlimited       = !!unlimited.forms;
  const isSubsUnlimited        = !!unlimited.submissions_this_month;
  const isSignedUnlimited      = !!unlimited.signed_docs_this_month;
  const isStorageUnlimited     = !!unlimited.storage;
  const resetInDays            = period_reset?.days_until_reset ?? null;

  const anyNearLimit = Object.values(percentages || {}).some((p) => p >= 80);

  return (
    <Card data-testid="usage-meters-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            Your Usage
            {isEnterprise && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
                <InfinityIcon className="w-3 h-3" /> Enterprise
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Current billing period · {plan_display_name} plan
          </CardDescription>
        </div>
        {!isEnterprise && anyNearLimit && upgrade_to && (
          <Button
            size="sm"
            onClick={() => navigate('/pricing')}
            data-testid="usage-meters-upgrade-cta"
            className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Upgrade to {upgrade_to}
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        <MeterRow
          icon={FormInput}
          label="Active forms"
          current={current.forms}
          limit={limits.max_forms}
          pct={percentages.forms}
          isUnlimited={isFormsUnlimited}
          testId="meter-forms"
        />
        <MeterRow
          icon={Database}
          label="Submissions this month"
          current={current.submissions_this_month}
          limit={limits.max_submissions_per_month}
          pct={percentages.submissions_this_month}
          isUnlimited={isSubsUnlimited}
          resetInDays={resetInDays}
          testId="meter-submissions"
        />
        <MeterRow
          icon={FilePen}
          label="Signed documents this month"
          current={current.signed_docs_this_month}
          limit={limits.max_signed_documents_per_month}
          pct={percentages.signed_docs_this_month}
          isUnlimited={isSignedUnlimited}
          resetInDays={resetInDays}
          testId="meter-signed-docs"
        />
        <MeterRow
          icon={HardDrive}
          label="File storage"
          current={current.storage_bytes}
          limit={limits.storage_bytes}
          pct={percentages.storage}
          isStorage
          isUnlimited={isStorageUnlimited}
          testId="meter-storage"
        />
      </CardContent>
    </Card>
  );
};
